"use client";

import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import ReactMarkdown, { type Components } from "react-markdown";
import { useBoard } from "@/components/BoardProvider";
import { getFaqs } from "@/lib/faqs";

// Renders FAQ answers written in markdown. Links open in a new tab (external)
// while internal routes stay in-tab; lists/emphasis render inline. The accordion
// container owns spacing/list styling via the wrapper classes below.
const markdownComponents: Components = {
  a({ href, children }) {
    const isExternal = /^https?:\/\//.test(href ?? "");
    return isExternal ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[var(--primary)] underline underline-offset-2 hover:no-underline"
      >
        {children}
      </a>
    ) : (
      <a
        href={href}
        className="text-[var(--primary)] underline underline-offset-2 hover:no-underline"
      >
        {children}
      </a>
    );
  },
};

export function FaqSection() {
  const board = useBoard();
  const faqs = getFaqs(board);

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="max-w-3xl mx-auto px-6 py-16 space-y-6">
      <h2 className="text-3xl md:text-4xl font-script text-center mb-8 text-[var(--foreground)]">
        Frequently Asked Questions
      </h2>
      <div className="space-y-4">
        {faqs.map((faq, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div
              key={idx}
              className="border border-[var(--border)] rounded-lg overflow-hidden bg-[var(--card)]
                hover:border-[var(--primary)] transition-all"
            >
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : idx)}
                className="w-full flex cursor-pointer items-center justify-between gap-4 px-6 py-4 text-left focus:outline-none"
              >
                <span className="text-lg md:text-xl font-polysans text-[var(--foreground)]">
                  {faq.question}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-[var(--foreground)] transform transition-transform duration-300 ${isOpen ?
                    "rotate-180" : "rotate-0"}`}
                />
              </button>
              {isOpen && (
                <div
                  className="px-6 pb-6 text-[var(--muted-foreground)] font-polysans leading-relaxed
                    [&_p]:my-3 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0
                    [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:my-1
                    [&_strong]:text-[var(--foreground)] [&_strong]:font-semibold"
                >
                  <ReactMarkdown components={markdownComponents}>
                    {faq.answer}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
